/* eslint-disable max-lines-per-function */
import { Button, ButtonGroup, Spacing, Subhead, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import eightGb from 'img/public/benefitsOfAuth/8gb.png';
import blueFolderWithPencil from 'img/public/benefitsOfAuth/blueFolderWithPencil.png';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';

import styles from './PublicBenefitsOfAuthorizationModal.css';

export interface IPublicBenefitsOfAuthorizationModalProps {
    theme: 'color' | 'no-color';
    title: string;
    subtitle: string;
    buttons: Array<{
        onClick?(): void;
        appearance: 'accent' | 'neutral' | 'overlay';
        text: string;
        href?: string;
        qaId?: string;
    }>;
    closable?: boolean;
    onShow(): void;
    onClose(): void;
    onXClose(): void;
    onTapOutsideClose(): void;
}

export const PublicBenefitsOfAuthorizationModal = memo<IPublicBenefitsOfAuthorizationModalProps>(
    ({ theme, title, subtitle, buttons, onClose, onShow, onXClose, onTapOutsideClose, closable = true }) => {
        const scrollRef = useRef<HTMLDivElement>(null);
        const isColorTheme = theme === 'color';

        useEffect(() => {
            onShow();
        }, [onShow]);

        const handleButtonClick = useCallback(
            (button: { onClick?(): void }) => {
                button.onClick?.();
                onClose();
            },
            [onClose]
        );

        const innerClassNames = useMemo(
            () => ({
                close: !isColorTheme ? styles.darkCloseButton : undefined,
            }),
            [isColorTheme]
        );

        return (
            <MobileDialog
                id="public-benefits-auth-mob"
                onClose={onClose}
                onDimmerClick={onTapOutsideClose}
                onCloseButtonClick={onXClose}
                topmost
                closeOnDimmerClick={closable}
                scrollRef={scrollRef}
                mod="base"
                closable={closable}
                contextPadding={16}
                closeMode={isColorTheme ? 'lightAndRound' : 'darkAndRound'}
                className={classNames({
                    [styles.colorDialog]: theme === 'color',
                    [styles.noColorDialog]: theme === 'no-color',
                })}
                innerClassNames={innerClassNames}
            >
                <div ref={scrollRef} className={styles.rootMobile}>
                    <div className={styles.imageContainer}>
                        {theme === 'no-color' ? (
                            <img src={blueFolderWithPencil} className={styles.image} />
                        ) : (
                            <img src={eightGb} className={styles.image} />
                        )}
                    </div>
                    <Title
                        level="1"
                        className={classNames({
                            [styles.title]: true,
                            [styles.title_color]: isColorTheme,
                        })}
                        data-qa-id="public-benefits-auth-mob-header"
                    >
                        {title}
                    </Title>
                    <Subhead
                        weight="3"
                        className={classNames({
                            [styles.subhead]: true,
                            [styles.subhead_color]: isColorTheme,
                        })}
                        data-qa-id="public-benefits-auth-mob-content"
                    >
                        {subtitle}
                    </Subhead>
                    <Spacing size={24} />
                    <ButtonGroup mode="vertical" gap="m" stretched>
                        {buttons.map((button, index) => (
                            <Button
                                key={index}
                                size="l"
                                mode="primary"
                                appearance={button.appearance}
                                onClick={() => handleButtonClick(button)}
                                stretched
                                Component={button.href ? 'a' : 'button'}
                                href={`${button.href}?mt_deeplink=${window.location.href}`}
                                className={classNames({
                                    [styles.button_accent_color]: button.appearance === 'accent' && isColorTheme,
                                })}
                                data-qa-id={button.qaId}
                            >
                                {button.text}
                            </Button>
                        ))}
                    </ButtonGroup>
                </div>
            </MobileDialog>
        );
    }
);

PublicBenefitsOfAuthorizationModal.displayName = 'PublicBenefitsOfAuthorizationModal';
